import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@aimsos/hooks';

import {
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
  SEARCH_DEBOUNCE_MS,
  SEARCH_FIELDS,
} from '../constants/dataListFilters.js';

const EMPTY_FILTERS = Object.freeze({});

/**
 * Single source of truth for a list's search, filters and sort — the entity
 * list and every dynamic profile tab both run on this.
 *
 * Modelled on `useWidgetLibraryFilters` in `apps/dashboards`, with one
 * difference that matters: the Widget Library knows its dimensions at build
 * time and keeps each as a named scalar. Here they arrive with the rows, so the
 * state is a map keyed by whatever ids the payload sent — the hook never names
 * a dimension.
 *
 * The sort defaults are the entity list's (name, ascending) unless a list says
 * otherwise: the Activity feed is newest-first (CT-5527). They are also what
 * "Clear all" returns to, so a feed never flips order when its filters clear.
 *
 * Nothing filters locally. The hook produces a memoised `query` that
 * `useEntityList` spreads into the request body, so the service (and later the
 * endpoint) does the narrowing. `query` is a dependency of that fetch, so it is
 * memoised on primitives only.
 */
export function useDataListFilters({
  defaultSortField = DEFAULT_SORT_FIELD,
  defaultSortDirection = DEFAULT_SORT_DIRECTION,
  searchFields = SEARCH_FIELDS,
} = {}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(EMPTY_FILTERS);
  // The bar's chips commit immediately; the "All filters" panel is STAGED —
  // it edits `draft`, and only `applyDraft()` moves that into `selected`, so
  // dismissing the panel changes nothing.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // One request per settled query, not one per keystroke.
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  // Clearing is not a keystroke and must not wait for the timer. Emptying the
  // box reads the debounced value for another 400ms otherwise, which makes the
  // no-results "Clear filters" button look like it did nothing.
  const effectiveSearch = searchTerm.trim() === '' ? '' : debouncedSearch.trim();

  // Serialised, then parsed back: `selected` is an object of arrays, so a new
  // identity every render would re-fetch forever. The string is the stable
  // primitive the memo can actually depend on.
  const selectedKey = JSON.stringify(selected);

  const filters = useMemo(() => {
    const entries = Object.entries(JSON.parse(selectedKey)).filter(
      ([, values]) => values?.length,
    );
    return entries.length ? Object.fromEntries(entries) : undefined;
  }, [selectedKey]);

  // Which fields the backend searches is the list's call, not the hook's: the
  // entity list searches title + description, the Activity feed title + preview
  // + AI summary. Depend on the content, not the array identity, so a caller's
  // inline literal does not re-fetch every render.
  const searchFieldsKey = searchFields.join(',');
  const search = useMemo(
    () => (effectiveSearch ? { query: effectiveSearch, fields: searchFieldsKey.split(',') } : undefined),
    [effectiveSearch, searchFieldsKey],
  );

  const activeFilterCount = useMemo(
    () => Object.values(JSON.parse(selectedKey)).filter((values) => values?.length).length,
    [selectedKey],
  );

  const hasActiveQuery = activeFilterCount > 0 || effectiveSearch.length > 0;

  const setFilter = useCallback((id, values) => {
    setSelected((current) => ({ ...current, [id]: values }));
  }, []);

  const setDraftFilter = useCallback((id, values) => {
    setDraft((current) => ({ ...current, [id]: values }));
  }, []);

  /** Seed the draft from what is committed — call this as the panel opens. */
  const openDraft = useCallback(() => setDraft(selected), [selected]);
  const applyDraft = useCallback(() => setSelected(draft), [draft]);
  const clearDraft = useCallback(() => setDraft(EMPTY_FILTERS), []);

  // Filters are the one thing that does NOT travel between record types: their
  // ids and options belong to a type. Search and sort mean the same everywhere.
  const resetFilters = useCallback(() => {
    setSelected(EMPTY_FILTERS);
    setDraft(EMPTY_FILTERS);
  }, []);

  const clearAll = useCallback(() => {
    setSearchTerm('');
    setSelected(EMPTY_FILTERS);
    setDraft(EMPTY_FILTERS);
    setSortField(defaultSortField);
    setSortDirection(defaultSortDirection);
  }, [defaultSortField, defaultSortDirection]);

  return useMemo(
    () => ({
      searchTerm,
      setSearchTerm,
      selected,
      setFilter,
      draft,
      setDraftFilter,
      openDraft,
      applyDraft,
      clearDraft,
      sortField,
      setSortField,
      sortDirection,
      setSortDirection,
      defaultSortField,
      defaultSortDirection,
      resetFilters,
      clearAll,
      activeFilterCount,
      hasActiveQuery,
      // What the request actually carries.
      filters,
      search,
      orderBy: sortField,
      orderDirection: sortDirection,
    }),
    [
      searchTerm, selected, setFilter, draft, setDraftFilter, openDraft, applyDraft,
      clearDraft, sortField, sortDirection, defaultSortField, defaultSortDirection,
      resetFilters, clearAll, activeFilterCount,
      hasActiveQuery, filters, search,
    ],
  );
}

export default useDataListFilters;
